import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useAuth } from '@hooks/useAuth';
import type { UseWebPushNotificationsReturn } from './useWebPushNotifications';
import { logger } from '@utils/logger';
import { defineStoredValue, FINITE_NUMBER } from '@utils/storedValue';
import { getWebPlatform, isPwaInstalled } from '@lib/pwa/install-utils';
import { createGrantFlow } from '@lib/overlay/grantFlow';

/**
 * The push surface this prompt actually reads. Both the legacy VAPID hook
 * (`useWebPushNotifications`) and the OneSignal opt-in adapter (`useOneSignalPush`)
 * satisfy it, so the same prompt + drawer choreography drives either path. Kept
 * as a `Pick` (rather than accepting the full return type) so the OneSignal
 * adapter — which has no `isVapidRotationFailed` — is assignable without change.
 */
type WebPushPromptSource = Pick<
  UseWebPushNotificationsReturn,
  'isSupported' | 'isSubscribed' | 'permission' | 'subscribe'
>;

const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const dismissedAtStore = defineStoredValue({
  storage: 'local',
  key: (userId: string) => `web_push_dismissed_${userId}`,
  ...FINITE_NUMBER,
});

/**
 * Module scope gives `useSyncExternalStore` a stable subscribe/getState identity
 * across renders; exactly one push initializer (OneSignal or legacy VAPID) ever
 * mounts, so there is only one turn to take (overlay-system-design §6.3, §6.6).
 */
const pushPromptFlow = createGrantFlow('push-prompt');

export interface UseWebPushPermissionPromptReturn {
  isOpen: boolean;
  shouldPrompt: boolean;
  isEnabling: boolean;
  show: () => void;
  handleEnable: () => Promise<void>;
  handleDismiss: () => void;
}

export const useWebPushPermissionPrompt = (
  webPush: WebPushPromptSource,
): UseWebPushPermissionPromptReturn => {
  const { user } = useAuth();
  const isOpen = useSyncExternalStore(
    pushPromptFlow.subscribe,
    () => pushPromptFlow.getState() === 'held',
  );

  const userId = user?.id;

  const shouldPrompt = useMemo(() => {
    if (!webPush.isSupported) return false;
    if (webPush.isSubscribed) return false;
    if (webPush.permission === 'denied') return false;
    if (webPush.permission === 'granted') return false;
    if (!userId) return false;
    const dismissedAt = dismissedAtStore.read(userId);
    if (dismissedAt !== null && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return false;
    // iOS Safari requires PWA to be installed for push to work
    if (getWebPlatform() === 'ios' && !isPwaInstalled()) return false;

    return true;
  }, [webPush.isSupported, webPush.isSubscribed, webPush.permission, userId]);

  // Re-read when the grant comes up: the user may have subscribed or signed out
  // meanwhile. Synced in a layout effect, not a passive one: layout effects across
  // the whole tree finish before any passive effect runs, so an earlier-sibling
  // passive effect that frees the mutex in the same commit the ref's value changes
  // can never run this predicate against the previous commit's value.
  const shouldPromptRef = useRef(shouldPrompt);
  useLayoutEffect(() => {
    shouldPromptRef.current = shouldPrompt;
  });

  // An unmounted host must never keep a grant with nothing on screen.
  useEffect(
    () => () => {
      pushPromptFlow.end('programmatic');
    },
    [],
  );

  // "Maybe later": the one path that writes the 7-day cooldown. A forced close
  // (enable, unmount, a closer) ends the grant without it.
  const handleDismiss = useCallback(() => {
    pushPromptFlow.end('user');
    if (userId) {
      dismissedAtStore.write(Date.now(), userId);
    }
  }, [userId]);

  const show = useCallback(() => {
    if (!shouldPrompt) return;
    pushPromptFlow.request({
      stillNeeded: () => shouldPromptRef.current,
    });
  }, [shouldPrompt]);

  const { subscribe } = webPush;
  const [isEnabling, setIsEnabling] = useState(false);

  const handleEnable = useCallback(async () => {
    setIsEnabling(true);
    try {
      await subscribe();
    } catch (error) {
      logger.error('Web push: failed to subscribe from permission prompt:', error);
    } finally {
      setIsEnabling(false);
      pushPromptFlow.end('programmatic');
    }
  }, [subscribe]);

  return { isOpen, shouldPrompt, isEnabling, show, handleEnable, handleDismiss };
};
